  import React, { useEffect, useRef, useState } from 'react';
  import Uppy from "@uppy/core";
  import Tus from "@uppy/tus";
  import { Dashboard } from "@uppy/react";
  import ImageEditor from '@uppy/image-editor';
  import styles from './CustomUploadUppy.module.scss';
  import CustomUploadStatusBar from './CustomUploadStatusBar.jsx';

  import {finalizeLogoUpload, finalizeUpload} from '../../../helpers/index.js'

  import '@uppy/core/dist/style.css'
  import '@uppy/dashboard/dist/style.css'

  import '@uppy/drag-drop/dist/style.css'
  import '@uppy/file-input/dist/style.css'
  import '@uppy/progress-bar/dist/style.css'
  import '@uppy/image-editor/dist/style.css'

  import Finnish from '@uppy/locales/lib/fi_FI';
  import French from '@uppy/locales/lib/fr_FR';
  import Swedish from '@uppy/locales/lib/sv_SE';

  const uppyLocales = {
    fi: Finnish,
    fr: French,
    sv: Swedish,
  };

  import { file_repository_file_name_is_unique_path } from '~/routes';

  const CustomUploadUppy = ({name, maxFileSize, maxNumberOfFiles, allowedFileTypes, permissions, height=250, isLogo = false, fileRepositoryFolderId = false,
    programmeId = null, uploadCallback = () => {}, disabled=false, useEditor=false, editorOpts={}, genThumbs = false, isForm = false,
    autoUpload: autoUploadInitial = true, minImageDimensions = null, variantSet, attachmentData = {}
  }) => {
    const autoUpload = autoUploadInitial && maxNumberOfFiles === 1;
    const cropperRef = useRef(null);
    const pendingRemoveId = useRef(null);
    const [count, setCount] = useState(0);
    const [editingFile, setEditingFile] = useState(null);
    const [uploadComplete, setUploadComplete] = useState(false);

    //Workaround for automatically open the editor
    const hasEditedRef = useRef(false);

    const  uppyError = (file, error, response) => {
      if (response.status == "login-required") {
        AutoSave.updateCountSaveStatus("session_error");
      }

      if (response && (response.status != 200)) {
        window.alert(I18n.t('upload_image_fail_message',{ scope: 'fe', name: file.name, error: response.error }));
      }
    }

    const updateCount = () => {
      setCount(uppy.getFiles().length)
    }
    const uppyLocale = uppyLocales[I18n.locale];
    const uppy = React.useMemo(() => {
      return new Uppy({
        restrictions: {
          allowedFileTypes: allowedFileTypes,
          maxNumberOfFiles: 1,
          maxTotalFileSize: 250 * 1024 * 1024, // additional config for file size 250MB
          maxFileSize: maxFileSize
        },
        locale: {
          ...uppyLocale,
          strings: {
            ...(uppyLocale?.strings||{}),
            dropPasteFiles: I18n.t(maxNumberOfFiles > 1 ? 'drop_paste_files' : 'drop_paste_file', {scope: 'uppy'}),
            browseFiles: I18n.t(maxNumberOfFiles > 1 ? 'browse_files' : 'browse_file', {scope: 'uppy'})
          },
        },
        autoProceed: false
      });
    }, []);

    const replaceFileRef = useRef(null);
    useEffect(() => {
      uppy.use(Tus, {
        endpoint: '/files',     // path to tus server
        chunkSize: 2*1024*1024, // 2MB chunks
        removeFingerprintOnSuccess: true,  // remove fingerprint after successful upload
      })

      if(useEditor) {
        uppy.use(ImageEditor, {
          cropperOptions: {
            ready() {
              cropperRef.current = this.cropper;
            },
            ...editorOpts.cropper
          },
          actions: editorOpts.actions,
        }).on('file-editor:cancel', (file) => {
          pendingRemoveId.current = file?.id || null;
          uppy.emit('dashboard:modal-closed');
        }).on('dashboard:modal-closed', () => {
          const id = pendingRemoveId.current;
          pendingRemoveId.current = null;
          if (id && uppy.getFile(id)) {
            uppy.removeFile(id);
            setEditingFile(null);
            updateCount();
          }
        }).on('file-editor:complete', (file) => {
          // Workaround to update the file after editing
          hasEditedRef.current = true;

          if (autoUpload) {
            uppy.upload();
          }
        }).on('file-editor:start', (file) => {
          setEditingFile(file);
          updateCount();
          hasEditedRef.current = false;
        });
      }

      const checkDims = (file, min) => {
        if (!min) return Promise.resolve(true);
        if (!file.type?.startsWith('image/') || file.type === 'image/svg+xml') return Promise.resolve(true);
        return new Promise((resolve) => {
          const img = new Image();
          const url = URL.createObjectURL(file.data);
          img.onload = () => { URL.revokeObjectURL(url); resolve(img.width >= min.width && img.height >= min.height); };
          img.onerror = () => { URL.revokeObjectURL(url); resolve(true); };
          img.src = url;
        });
      }


      uppy.on("file-added", async (file) => {
        const ok = await checkDims(file, minImageDimensions);
        if (!ok) {
          uppy.info(`Image must be at least ${minImageDimensions.width}x${minImageDimensions.height}`, 'error', 3000);
          uppy.removeFile(file.id);
          setEditingFile(null);
          setCount(0);
          setUploadComplete(false);
          return;
        }
    
        updateCount();
        setEditingFile(null);

        if (!hasEditedRef.current && useEditor && file.type.startsWith('image/') && file.type !== 'image/svg+xml') {
          setEditingFile(file);
          uppy.getPlugin('uppy-'+name).openFileEditor(file)
        }
        if (!fileRepositoryFolderId) {
          return;
        }
        // Checking file uniqueness on upload to file repository
        jQuery.get(file_repository_file_name_is_unique_path({project_id: programmeId, folder_id: fileRepositoryFolderId, file_name: file.name}), (isUnique) => {
          if (replaceFileRef.current !== null || isUnique === true) {
            return;
          }
          replaceFileRef.current = false;
          if (isUnique === false && confirm(I18n.t('files_not_unique_message'))) {
            replaceFileRef.current = true;
          }
        })
      }).on("file-removed", (file) => {
        updateCount();
        hasEditedRef.current = false;
        uploadCallback({attachment_id: ''});
      })
      
      uppy.on("complete", (result) => {
        setEditingFile(null);
        setUploadComplete(true);
        updateCount();
        const url = result.successful[0].uploadURL;
      });

      uppy.on('upload-success', (file, response) => {
        if (isLogo) {
          finalizeLogoUpload(file, response, permissions).then(res => {
            if (res.error) {
              uppyError(file, res.error, res)
            }
          });			
        }
        else if (fileRepositoryFolderId) {
          finalizeUpload(file, response, permissions).then(res => {
            if (res.error) {
              uppyError(file, res.error, res)
            } else {
              uploadCallback(res, replaceFileRef.current ?? false);
            }
          });
        }
        else {
          finalizeUpload(file, response, permissions, genThumbs, variantSet).then(res => {
            if (res.error) {
              uppyError(file, res.error, res)
            }
            if (isForm){
              jQuery(`input[name="${name}"]`).val(res.attachment_id)
            }
            uploadCallback(res);
          });			
        }
      });

      // If in edit mode, preload the existing file
      if (attachmentData['path']) {
        fetch(attachmentData['path'])
          .then(res => res.blob())
          .then(blob => {
            uppy.addFile({
              name: attachmentData['filename'],
              source: attachmentData['storage'],
              type: attachmentData['mime_type'],
              data: blob,
              isRemote: false,
            });

            jQuery(`input[name="${name}"]`).val(attachmentData['id'])
          });
      }
    }, [])

    const showStatusBar = count || editingFile || uploadComplete;
    return (
      <div className={`${styles['custom-image-uploader']} ${showStatusBar ? styles['has-files'] : ''}`}>
        <CustomUploadStatusBar
          uppy={uppy}
          editingFile={editingFile}
          count={count}
          uploadComplete={uploadComplete}
          cropperRef={cropperRef}
          editorOpts={editorOpts}
          show={showStatusBar}
          maxNumberOfFiles={maxNumberOfFiles}
          autoUpload={autoUpload}
        />
        <Dashboard id={'uppy-' + name}
          name={name}
          uppy={uppy}
          inline={true}
          showProgressDetails={false}
          proudlyDisplayPoweredByUppy={false}
          showSelectedFiles={true}
          showLinkToFileUploadResult={true}
          height={height}
          width='100%'
          doneButtonHandler = {null}
          disabled={disabled}
          plugins={['ImageEditor']}
          disableStatusBar
        />
      {isForm &&  <input name={name} hidden readOnly></input>}
      </div>
      );

  }

  export default CustomUploadUppy;
